import { ButtonProps, buttonVariants } from '@/components/Button';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import * as React from 'react';
import { FaEllipsisH } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  isArrow?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<typeof Link>;

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  isArrow = false,
  ...props
}: PaginationLinkProps) => (
  <Link
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'default',
        size,
      }),
      'size-11 flex justify-center items-center text-center border-0 text-base font-bold duration-200',
      isActive
        ? 'bg-blue text-white hover:bg-blue hover:text-white'
        : 'bg-gray-200 text-gray-850 hover:brightness-75 focus:brightness-75',
      !isArrow && !isActive ? 'hidden tablet:flex' : '',
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

type PaginationLinkInactiveProps = {
  isActive?: boolean;
  isArrow?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'div'>;

const PaginationLinkInactive = ({
  className,
  isActive,
  size = 'icon',
  isArrow = false,
  ...props
}: PaginationLinkInactiveProps) => (
  <div
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'default',
        size,
      }),
      'size-11 flex justify-center items-center text-center border-0 text-base font-bold',
      isActive
        ? 'bg-blue text-white hover:bg-blue hover:text-white'
        : 'bg-gray-200 text-gray-850 hover:bg-gray-200 hover:text-gray-850',
      className,
    )}
    {...props}
  />
);
PaginationLinkInactive.displayName = 'PaginationLinkInactive';

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    // size="default"
    className={className}
    isArrow
    {...props}>
    <FiChevronLeft className="w-5 h-5 mx-auto" />
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    // size="default"
    className={className}
    isArrow
    {...props}>
    <FiChevronRight className="w-5 h-5 mx-auto" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}>
    <FaEllipsisH className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationLinkInactive,
};
